import type { AwxConnectedAccount } from '@ankor-io/common/types/awx'
import type { ModuleInfo } from '@ankor-io/common/types/pay'
import { Banner } from '@ankor-io/ui-components/src/Banner'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Link } from '@ankor-io/ui-components/src/Link'

import { useModuleTheme } from '@/providers/moduleTheme'
import { getModuleContent } from '@/utils/moduleKycState'

const bannerButtonColor = {
  info: 'primary',
  warning: 'orange',
  error: 'red',
  success: 'green',
} as const

type AccountActivationPromptProps = {
  connectedAccount: AwxConnectedAccount
  moduleAvailability?: ModuleInfo | null
  onContinue: () => void
  onActivateModule?: () => void
  isLoading?: boolean
}

export const AccountActivationPrompt = ({
  connectedAccount,
  moduleAvailability = null,
  onContinue,
  onActivateModule,
  isLoading = false,
}: AccountActivationPromptProps) => {
  const { moduleTheme } = useModuleTheme()
  const { banner, cta } = getModuleContent(moduleAvailability, connectedAccount)

  if (!banner) return null

  return (
    <Banner
      variant={banner.variant}
      title={banner.title}
      description={banner.description}
    >
      {cta && (cta.to || cta.activateModule) && (
        <Button
          color={bannerButtonColor[banner.variant]}
          onClick={cta.activateModule ? onActivateModule : onContinue}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : cta.label}
        </Button>
      )}

      <p className="text-sm text-gray-500 dark:text-gray-400">
        Any questions please reach out to our{' '}
        <Link
          className={moduleTheme.link}
          to="https://ankorpay.productfruits.help/en/article/how-to-contact-support"
          target="_blank"
          rel="noopener noreferrer"
        >
          Support
        </Link>{' '}
        team.
      </p>
    </Banner>
  )
}
